// moduleConsole.js — el.call action helpers for the /admin/module console
// (pages/admin/module.js). Every backend touch is SDK-only (el.sdk.*) — zero
// raw fetch. The console lets an org owner/admin point the workspace at a
// module + version, manage the module's records capability grant
// (workspace_records via moduleDataBridge), personalize (navbar / apps /
// panels / designSystem), and roll a version back.
//
// Calling convention (matches greenigActions.js / moduleActions.js): these are
// registered functions invoked via `el.call('mcX', ...args)`, so inside each
// `this` IS the element. We read `el = this`, `root = el.state.root`, and the
// SDK off `el.sdk` (bootShell exposes context.methods.sdk as el.sdk). Explicit
// args (slug, keyName, value, version) ride after the name.
//
// State model — all console state lives under `_mc*` root keys so it's easy to
// reason about and never collides with shell/module state:
//   _mcState          { loaded, loading, error, wsId, orgId, orgSlug, settings }
//   _mcTab            active tab id
//   _mcModuleDraft    { owner, key, channel, version, tier }
//   _mcVersions       { loading, error, list, envs }
//   _mcDs             { text, parsed, error, validated }
//   _mcBusy           { assign, rollback, personalize, uninstall }
//   _mcGrantAppId     Grant tab's selected app ("owner/key") — workspaceModule
//                      or one entry of the plural workspaceApps list
//   _mcSyncChecking   the app id currently being manifest-diffed, or null
//   _mcSyncDiff        { appId, declared, granted, added, stale, checkedAt }
//   _mcSyncSelection   { grant: {collection: bool}, revoke: {collection: bool} }
//                      (bucket names deliberately avoid signal-store reserved
//                      method names — `add` collided; see STATE_METHODS in
//                      @symbo.ls/state src/methods.js)
//   _mcSyncError       last collections-sync check error message, or null
//
// Persistence is merge-safe: settings writes go through
// `el.sdk.updateWorkspaceSettings(wsId, partialSettings)` with ONLY the changed
// key(s) — never the whole settings bag. Each mutating action optimistically
// patches `root._mcState.settings`, then persists; a failure rolls the
// optimistic patch back and surfaces a Notification.

import { applyWorkspaceConfig } from './workspaceConfigs.js'
import { loadWorkspaceProject, loadWorkspaceApps } from './workspaceProjects.js'
// THE accessor for `root.workspace(.settings)` (spec §5). `workspaceSettings`
// for the id-only read in `_ids`; `workspaceSettingsForWrite` for the three
// optimistic post-write mirrors below, which are WRITE-BASE sites and must not
// merge over a base they do not hold.
import _wsShared from '@symbo-ls/workspace-shared'
import { workspaceSettingsForWrite } from './shellSharedDelegates.js'
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)
const diffDeclaredCollections = (...args) => _wsShared.functions.diffDeclaredCollections(...args)
const applyCollectionsSync = (...args) => _wsShared.functions.applyCollectionsSync(...args)
const upsertWorkspaceApp = (...args) => _wsShared.functions.upsertWorkspaceApp(...args)
const recordsEntryKey = (...args) => _wsShared.functions.recordsEntryKey(...args)
// Dropped when a mirror is skipped: with no local patch to read, the loaders
// re-read the record from the server, and an 8s-TTL cached copy from BEFORE
// this console's write would hand them the pre-write settings.
import { invalidateWorkspaceCache } from './getWorkspaceCached.js'
// The settings keys this console is allowed to write (mirrors the server-side
// whitelist on PATCH /workspaces/:id/settings). Personalize + Module tabs only
// ever persist one of these at a time.
export const MC_SETTINGS_KEYS = [
  'workspaceModule',
  'workspaceApps',
  'navbar',
  'apps',
  'home_default_panels',
  'designSystem'
]

// ── small internal helpers (single-file, pure) ────────────────────────────

const _sdkOf = (el) =>
  (el && el.sdk) || (el && el.context && el.context.methods && el.context.methods.sdk) || null

const _consoleRootOf = (el) => (el && el.state && el.state.root) || null

// Toast wrapper — openNotification is a house method (shared/methods/
// notification.js). Swallow its throw on standalone surfaces where the toast
// infra isn't wired (mirrors IntegrationConfigureForm._toast).
const _consoleNotify = (el, payload) => {
  try {
    el.call('openNotification', payload)
  } catch (err) {
    console.warn(
      '[moduleConsole]',
      payload && payload.title,
      (payload && payload.message) || '',
      err
    )
  }
}

const _orgSlugFromClaims = (claims) => {
  const orgId = claims && claims.workspace_org_id
  const orgs = (claims && claims.orgs) || []
  const o = orgs.find((x) => (x.id || x._id) === orgId)
  return (o && (o.slug || o.username)) || null
}

// Resolve the ids the console needs from root, tolerant of the leaned-claims
// shape (root.workspace can lag behind an org switch — see workspaceProjects.js).
const _ids = (root) => {
  // DECISION site (spec §5), NOT a write-base one — despite every write in this
  // file riding on the `wsId` it returns. What is read here is IDENTITY, and
  // identity is the one thing a list/claims-shaped record carries correctly:
  // it is missing `settings`, never `id`. Gating this on `ready` would strand
  // the console on exactly the boots where it is most needed (the record is
  // still in flight, but the workspace it refers to is already unambiguous),
  // and `updateWorkspaceSettings` is per-key merge-safe server-side, so a write
  // addressed by a correct id is safe regardless of what we hold locally. The
  // unsafe part is the LOCAL mirror — guarded separately, at each mirror.
  const { id: wsId } = shellWorkspaceSettings(root)
  const org = root.org || {}
  const claims = root.jwtClaims || {}
  return {
    wsId: wsId || null,
    orgId: claims.workspace_org_id || org.id || org._id || null,
    orgSlug: org.slug || org.username || _orgSlugFromClaims(claims) || null
  }
}

// Merge-safe optimistic patch of the console's working settings copy. Returns
// the previous value of the FIRST patched key so the caller can roll back.
const _patchSettings = (root, patch) => {
  const st = root._mcState || {}
  const prevSettings = st.settings || {}
  const firstKey = Object.keys(patch)[0]
  const prevValue = firstKey ? prevSettings[firstKey] : undefined
  root.update({ _mcState: { ...st, settings: { ...prevSettings, ...patch } } })
  return prevValue
}

const _setBusy = (root, key, val) => {
  root.update({ _mcBusy: { ...(root._mcBusy || {}), [key]: val } })
}

// ── workspaceApps compare-and-swap ─────────────────────────────────────────
// `settings.workspaceApps[]` is written as a WHOLE ARRAY by five different
// callers (marketplace install, /add-app install, both uninstalls, and this
// console's records-grant save), so two of them used to silently clobber each
// other — and each entry's `capabilities.records.collections` is an
// AUTHORIZATION field. `PATCH /core/workspaces/:id/settings` therefore
// preserves a still-installed app's stored capabilities UNLESS the writer
// proves freshness by echoing back the array it read, as `expectedApps`; a
// stale echo is refused with 409 instead of applied. This console is where
// deliberate grant/revoke edits happen, so it always sends the precondition.
const _appsPatch = (root, patch) => {
  if (!('workspaceApps' in patch)) return patch
  return { ...patch, expectedApps: (root && root._mcAppsBaseline) || [] }
}

// After a successful write our payload IS what the server now holds, so it
// becomes the baseline for the next save — no extra round-trip.
const _rebaseApps = (root, value) => {
  root.update({
    _mcAppsBaseline: Array.isArray(value) ? JSON.parse(JSON.stringify(value)) : []
  })
}

const _isAppsConflict = (err) =>
  /apps_version_conflict|changed since you read it/i.test((err && err.message) || '')

// A 409 is not a failure the operator caused — someone else installed or
// uninstalled an app while this tab was open. Re-read so the tab shows the
// truth, and say plainly that the edit has to be redone on top of it.
const _handleSaveError = async (el, root, err, failTitleKey, fallbackMessage) => {
  if (!_isAppsConflict(err)) {
    return _consoleNotify(el, {
      title: el.call('polyglot', failTitleKey),
      message: (err && err.message) || fallbackMessage || '',
      type: 'error'
    })
  }
  await el.call('mcLoadState')
  _consoleNotify(el, {
    title: el.call('polyglot', 'admin.module.notify.appsChanged'),
    message: el.call('polyglot', 'admin.module.notify.appsChangedBody'),
    type: 'error'
  })
}

// Unwrap the SDK envelope ({success,data,message}) → data, tolerant of a bare
// payload (some read methods return raw).
const _data = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res)

// Validate a designSystem editor payload shape
// ({canvasBg?, vars?, varsLight?, varsDark?}). Returns the error sentence
// (resolved through `el`), or null when the shape is acceptable.
//
// A `vars` value may be a scalar OR a `{ light, dark }` pair, and the two
// sibling channels `varsLight` / `varsDark` carry the same fact one level up.
// All three exist because `vars` alone holds ONE value per token, which cannot
// express a token whose light half is a gradient and whose dark half is a
// colour — `light-dark()` takes two <color>s, so that record paints nothing
// (FW-DESIGN-SYSTEM-RECORD-NEEDS-PER-THEME-VARS-1; the reader is
// workspaceConfigs.js resolveDesignSystemVars).
const _validateDsShape = (obj, el) => {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    return el.call('polyglot', 'admin.module.error.dsNotObject')
  }
  if ('canvasBg' in obj && obj.canvasBg != null && typeof obj.canvasBg !== 'string') {
    return el.call('polyglot', 'admin.module.error.dsCanvasBg')
  }
  const isScalar = (v) => typeof v === 'string' || typeof v === 'number'
  const isPair = (v) =>
    !!v && typeof v === 'object' && !Array.isArray(v) && ('light' in v || 'dark' in v)
  // The three channels share one error vocabulary: `dsVars` names a container
  // that is not an object of name → value pairs, `dsVarType` names the token
  // whose value is neither a scalar nor a well-formed { light, dark } pair.
  for (const channel of ['vars', 'varsLight', 'varsDark']) {
    if (!(channel in obj) || obj[channel] == null) continue
    const bag = obj[channel]
    if (typeof bag !== 'object' || Array.isArray(bag)) {
      return el.call('polyglot', 'admin.module.error.dsVars')
    }
    for (const [k, v] of Object.entries(bag)) {
      // A pair is legal in `vars` only — inside varsLight/varsDark it would be
      // a theme nested in a theme, which has no reading.
      if (channel === 'vars' && isPair(v)) {
        const halves = [v.light, v.dark].filter((h) => h != null)
        if (halves.length && halves.every(isScalar)) continue
        return el.call('polyglot', 'admin.module.error.dsVarType').replace('{name}', k)
      }
      if (isScalar(v)) continue
      return el.call('polyglot', 'admin.module.error.dsVarType').replace('{name}', k)
    }
  }
  return null
}

// ── 1. Load state ──────────────────────────────────────────────────────────
// getWorkspace(wsId) → settings (the whole console reads/writes settings only).
export async function mcLoadState() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const { wsId, orgId, orgSlug } = _ids(root)

  root.update({
    _mcState: { ...(root._mcState || {}), loading: true, error: null, wsId, orgId, orgSlug }
  })

  if (!sdk || !wsId) {
    root.update({
      _mcState: {
        ...(root._mcState || {}),
        loading: false,
        loaded: true,
        error: el.call(
          'polyglot',
          !wsId ? 'admin.module.error.noWorkspace' : 'admin.module.notify.sdkNotReady'
        ),
        settings: (root._mcState && root._mcState.settings) || {}
      }
    })
    return
  }

  let settings = {}
  let error = null
  try {
    const res = await sdk.getWorkspace(wsId)
    const full = _data(res) || {}
    settings = full.settings || {}
  } catch (err) {
    error = (err && err.message) || el.call('polyglot', 'admin.module.error.loadFailed')
  }

  const dsText = settings.designSystem ? JSON.stringify(settings.designSystem, null, 2) : ''

  root.update({
    _mcState: { loaded: true, loading: false, error, wsId, orgId, orgSlug, settings },
    // The SERVER's copy of the app list, kept untouched by the optimistic
    // `_patchSettings` writes below — it is the compare-and-swap precondition
    // (`expectedApps`) every `workspaceApps` save sends. `_mcState.settings`
    // cannot serve that role: the Grant tab mutates it locally on every
    // revoke/op-toggle before Save is ever pressed, so by save time it is the
    // DESIRED state, not the observed one.
    _mcAppsBaseline: Array.isArray(settings.workspaceApps)
      ? JSON.parse(JSON.stringify(settings.workspaceApps))
      : [],
    _mcDs: {
      text: dsText,
      parsed: settings.designSystem || null,
      error: null,
      validated: Boolean(settings.designSystem)
    },
    // Seed the module-assignment draft from the current reference (defaulting
    // owner = workspace org slug for Tier-1 self-serve).
    _mcModuleDraft: {
      owner: (settings.workspaceModule && settings.workspaceModule.owner) || orgSlug || '',
      key: (settings.workspaceModule && settings.workspaceModule.key) || '',
      channel: (settings.workspaceModule && settings.workspaceModule.channel) || 'production',
      version: (settings.workspaceModule && settings.workspaceModule.version) || '',
      tier: (settings.workspaceModule && settings.workspaceModule.tier) || 'trusted'
    }
  })
}

// ── 2. Enumerate module project versions (Module + Versions tabs) ──────────
export async function mcListModuleVersions() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const draft = root._mcModuleDraft || {}
  const owner = draft.owner
  const key = draft.key
  if (!owner || !key) {
    return root.update({
      _mcVersions: {
        loading: false,
        error: el.call('polyglot', 'admin.module.error.pickRef'),
        list: [],
        envs: []
      }
    })
  }

  root.update({ _mcVersions: { ...(root._mcVersions || {}), loading: true, error: null } })
  try {
    const project = _data(await sdk.getProjectByKey({ owner, key }))
    const projectId = project && (project._id || project.id)
    if (!projectId) {
      throw new Error(
        el
          .call('polyglot', 'admin.module.error.projectNotFound')
          .replace('{ref}', `${owner}/${key}`)
      )
    }

    let list = []
    let envs = []
    try {
      const v = _data(await sdk.getProjectVersions(projectId, { branch: draft.branch || 'main' }))
      list = Array.isArray(v) ? v : (v && v.versions) || []
    } catch (err) {
      console.warn('[moduleConsole] getProjectVersions failed', err && err.message)
    }
    try {
      const e = _data(await sdk.listEnvironments(projectId))
      const map = (e && e.environments) || {}
      envs = Object.keys(map).map((k) => ({ key: k, ...map[k] }))
    } catch (err) {
      console.warn('[moduleConsole] listEnvironments failed', err && err.message)
    }
    root.update({ _mcVersions: { loading: false, error: null, list, envs, projectId } })
  } catch (err) {
    root.update({
      _mcVersions: {
        loading: false,
        error: (err && err.message) || el.call('polyglot', 'admin.module.error.lookupFailed'),
        list: [],
        envs: []
      }
    })
  }
}

// ── 3. Assign the module (settings.workspaceModule) ────────────────────────
// The assign switches the module for EVERYONE in the workspace, so it asks
// first through the house confirm (CRUD-CONFIRM-STANDARD D1, 2026-10-30 — it
// wrote on one click). The write, `_assignModuleNow`, runs in `onConfirm`
// only; Cancel, ESC, the backdrop and the X write nothing.
export function mcAssignModule() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const draft = root._mcModuleDraft || {}
  const { wsId } = _ids(root)
  if (!draft.owner || !draft.key) {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.missingRef'),
      message: el.call('polyglot', 'admin.module.notify.missingRefBody'),
      type: 'error'
    })
  }
  if (typeof sdk.updateWorkspaceSettings !== 'function') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      message: el.call('polyglot', 'admin.module.notify.sdkNotReadyBody'),
      type: 'error'
    })
  }
  el.call('confirmDialog', {
    title: el
      .call('polyglot', 'admin.module.assign.confirmTitle')
      .replace('{module}', `${draft.owner}/${draft.key}`),
    message: el.call('polyglot', 'admin.module.assign.confirmMessage'),
    confirmText: el.call('polyglot', 'admin.module.assign.save'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: () => _assignModuleNow(el, root, sdk, draft, wsId)
  })
}

async function _assignModuleNow(el, root, sdk, draft, wsId) {
  // Spread the existing module ref first so server-managed fields (capabilities
  // grant edited on the Grant tab, resolved-version metadata, install stamps)
  // survive a re-assign — then override only the fields the form edits.
  const prevMod =
    (root._mcState && root._mcState.settings && root._mcState.settings.workspaceModule) || null
  const workspaceModule = {
    ...(prevMod || {}),
    type: 'project',
    owner: draft.owner,
    key: draft.key,
    channel: draft.channel || 'production',
    version: draft.version || null,
    tier: draft.tier || 'trusted'
  }

  // Auto-grant on first assign: nothing else writes capabilities.records
  // (WORKSPACE_APPS.md ground truth), so an assign without a grant leaves the
  // module data bridge with nothing to allow. Grant the module its OWN
  // declared collections; an existing grant (Grant tab / re-assign of the
  // same module) is preserved untouched by the spread above.
  const prevCols =
    workspaceModule.capabilities &&
    workspaceModule.capabilities.records &&
    workspaceModule.capabilities.records.collections
  const sameModule = prevMod && prevMod.owner === draft.owner && prevMod.key === draft.key
  if (!(sameModule && Array.isArray(prevCols) && prevCols.length)) {
    // el.call, NOT a bare reference: fetchDeclaredCollections is a registered
    // function exported from workspaceApps.js, not imported here — a bare call
    // threw `fetchDeclaredCollections is not defined` and silently failed every
    // FRESH module assign (this branch only runs on a new/changed module).
    // See tickets/workspace.md. FA001: cross-file fns go through el.call.
    const declared = await el.call('fetchDeclaredCollections', draft.owner, draft.key)
    if (declared.length) {
      workspaceModule.capabilities = {
        ...(sameModule ? workspaceModule.capabilities || {} : {}),
        records: {
          collections: declared,
          ops: ['list', 'get', 'create', 'update', 'upsert', 'remove', 'subscribe']
        }
      }
    } else if (!sameModule) {
      delete workspaceModule.capabilities
    }
  }

  const prev = _patchSettings(root, { workspaceModule })
  _setBusy(root, 'assign', true)
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, { workspaceModule })
    // The server's own words, or none: every catch below owns a translated
    // title (and, where it has one, a translated body) — no client sentence.
    if (res && res.success === false) throw new Error(res.message)
    _consoleNotify(el, {
      title: el
        .call('polyglot', 'admin.module.notify.assigned')
        .replace('{module}', `${draft.owner}/${draft.key}`),
      type: 'success'
    })
  } catch (err) {
    _patchSettings(root, { workspaceModule: prev })
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.assignFailed'),
      message: (err && err.message) || el.call('polyglot', 'admin.module.notify.assignFailedBody'),
      type: 'error'
    })
  } finally {
    _setBusy(root, 'assign', false)
  }
}

// ── 4. Personalize (navbar / apps / home_default_panels / designSystem) ─────
// Each Save persists ONLY that one settings key (merge-safe). keyName must be
// one of MC_SETTINGS_KEYS.
export async function mcSavePersonalization(keyName, value) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  if (MC_SETTINGS_KEYS.indexOf(keyName) < 0) {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.unsupportedSetting'),
      message: keyName,
      type: 'error'
    })
  }
  // An EMPTY navbar list is not a personalization — it's "render zero header
  // tabs", which no workspace can want. Persist the cleared-key sentinel
  // (null) instead so resolution falls through to the module's
  // configDefaults.navbar / DEFAULT_NAVBAR, exactly like mcResetNavbar.
  // Without this, Save-after-Reset (the working copy is null, the Save button
  // reads `navbar || []`) — or removing every row and saving — stored
  // `settings.navbar: []`, which used to blank every header tab. Other list
  // keys keep [] (an empty home_default_panels is a deliberate hide-all).
  if (keyName === 'navbar' && Array.isArray(value) && !value.length) value = null
  if (typeof sdk.updateWorkspaceSettings !== 'function') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      message: el.call('polyglot', 'admin.module.notify.sdkNotReadyBody'),
      type: 'error'
    })
  }

  const prev = _patchSettings(root, { [keyName]: value })
  _setBusy(root, 'personalize', true)
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, _appsPatch(root, { [keyName]: value }))
    if (res && res.success === false) throw new Error(res.message)
    if (keyName === 'workspaceApps') _rebaseApps(root, value)
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.saved'),
      message: keyName,
      type: 'success'
    })
  } catch (err) {
    _patchSettings(root, { [keyName]: prev })
    await _handleSaveError(el, root, err, 'admin.module.notify.saveFailed', keyName)
  } finally {
    _setBusy(root, 'personalize', false)
  }
}

// ── 5. Roll a version back (re-pin settings.workspaceModule.version) ───────
// Pin, Re-pin and Unpin change the version EVERYONE in the workspace runs, so
// each asks first through the house confirm (CRUD-CONFIRM-STANDARD D1,
// 2026-10-30 — they wrote on one click). The write runs in `onConfirm` only.
export function mcRollbackModule(version) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  const cur =
    (root._mcState && root._mcState.settings && root._mcState.settings.workspaceModule) || null
  if (!cur)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.noModule'),
      type: 'error'
    })
  if (typeof sdk.updateWorkspaceSettings !== 'function') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      message: el.call('polyglot', 'admin.module.notify.sdkNotReadyBody'),
      type: 'error'
    })
  }
  el.call('confirmDialog', {
    title: version
      ? el.call('polyglot', 'admin.module.versions.pinTitle').replace('{version}', version)
      : el.call('polyglot', 'admin.module.versions.unpinTitle'),
    message: el.call(
      'polyglot',
      version ? 'admin.module.versions.pinMessage' : 'admin.module.versions.unpinMessage'
    ),
    confirmText: el.call(
      'polyglot',
      version ? 'admin.module.versions.pinCta' : 'admin.module.versions.unpin'
    ),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: () => _rollbackModuleNow(el, root, sdk, wsId, cur, version)
  })
}

async function _rollbackModuleNow(el, root, sdk, wsId, cur, version) {
  const workspaceModule = { ...cur, version: version || null }
  const prev = _patchSettings(root, { workspaceModule })
  root.update({ _mcModuleDraft: { ...(root._mcModuleDraft || {}), version: version || '' } })
  _setBusy(root, 'rollback', true)
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, { workspaceModule })
    if (res && res.success === false) throw new Error(res.message)
    _consoleNotify(el, {
      title: version
        ? el.call('polyglot', 'admin.module.notify.pinned').replace('{version}', version)
        : el.call('polyglot', 'admin.module.notify.unpinned'),
      type: 'success'
    })
  } catch (err) {
    _patchSettings(root, { workspaceModule: prev })
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.rollbackFailed'),
      message: (err && err.message) || '',
      type: 'error'
    })
  } finally {
    _setBusy(root, 'rollback', false)
  }
}

// ── Personalize working-copy list editors (no persist — Save does that) ────
// navbar [{label,path}] and apps [{title,caption,icon,iconBg,route}] are edited
// against the console's working settings copy; the section's Save button then
// persists the whole array through mcSavePersonalization(key, value).

const _listOf = (root, key) => {
  const list = root._mcState && root._mcState.settings && root._mcState.settings[key]
  return Array.isArray(list) ? list : []
}

export function mcEditListItem(key, idx, field, value) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const list = _listOf(root, key).map((it, i) => (i === idx ? { ...it, [field]: value } : it))
  _patchSettings(root, { [key]: list })
}

export function mcAddListItem(key, template) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  _patchSettings(root, { [key]: [..._listOf(root, key), { ...(template || {}) }] })
}

// NAVBAR-REMOVE-PERSIST-1 (2026-08-18): a navbar row an installed app OR the
// assigned module contributes on its OWN (root.workspaceApps[].navbar /
// root.workspaceProject.config.navbar) is re-derived on every
// applyWorkspaceConfig pass (workspaceConfigs.js _appendAppsNavbar /
// resolveWorkspaceConfig) — a union-over-defaults with NO way to tell
// "never added yet" from "explicitly removed". A plain filter-out here
// looked like it worked (the in-session mirror + the very next Save both
// read back clean), but the FIRST re-derivation — the very next
// applyWorkspaceConfig pass, which fires on every boot/reload — silently
// re-appended the row, because `_appendNavbar`'s dedup only checks "is this
// path in the array right now", never "was it explicitly removed". Root
// cause + fix both reproduced end-to-end in
// moduleConsoleNavbarRemove.test.js (this directory).
//
// Fix: leave a `{ path, dismissed: true }` tombstone in the row's place
// instead of dropping it outright. It still occupies the path in
// `_appendNavbar`'s `seen` set (so nothing re-adds it) while
// `navItemVisible` (shared/functions/featureFlags.js) hides it from every
// renderer (HeaderTabs, this console's own Rows list). A row that is NOT
// app/module-contributed (a built-in default, or a manually-added custom
// row) is never re-derived from anywhere, so it is still dropped outright —
// a tombstone for it would just be permanent clutter.
export function mcRemoveListItem(key, idx) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const list = _listOf(root, key)
  const removed = list[idx]
  let next = list.filter((_, i) => i !== idx)
  if (key === 'navbar' && removed && removed.path && !removed.dismissed) {
    const contributedPaths = new Set(
      [
        ...(Array.isArray(root.workspaceApps) ? root.workspaceApps : []).flatMap((a) =>
          Array.isArray(a && a.navbar) ? a.navbar : []
        ),
        ...(root.workspaceProject && Array.isArray(root.workspaceProject.config?.navbar)
          ? root.workspaceProject.config.navbar
          : [])
      ]
        .map((t) => t && t.path && String(t.path).toLowerCase())
        .filter(Boolean)
    )
    if (contributedPaths.has(String(removed.path).toLowerCase())) {
      next = [...next, { path: removed.path, dismissed: true }]
    }
  }
  _patchSettings(root, { [key]: next })
}

// Reorder a working-copy list item — the Navbar row drag handle / Up-Down
// buttons (pages/admin/module.js AdminModuleNavbarRow). No persist here —
// Save does that, exactly like mcEditListItem/mcAddListItem/mcRemoveListItem
// above. Generic over `key` so the same primitive covers `apps` too.
export function mcMoveListItem(key, fromIdx, toIdx) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const list = _listOf(root, key)
  if (
    fromIdx === toIdx ||
    fromIdx < 0 ||
    toIdx < 0 ||
    fromIdx >= list.length ||
    toIdx >= list.length
  ) {
    return
  }
  const next = list.slice()
  const [moved] = next.splice(fromIdx, 1)
  next.splice(toIdx, 0, moved)
  _patchSettings(root, { [key]: next })
}

// Toggle a home_default_panels id in the working copy (Save persists it).
export function mcTogglePanel(id) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const cur = _listOf(root, 'home_default_panels')
  const next = cur.indexOf(id) >= 0 ? cur.filter((x) => x !== id) : [...cur, id]
  _patchSettings(root, { home_default_panels: next })
}

// ── Records capability grant (Grant tab) ────────────────────────────────────
// `workspaceModule.capabilities.records = { collections, ops }` is the grant
// moduleDataBridge.js enforces for the module's generic-record access
// (client-side check is defense-in-depth only — the server enforces the same
// grant on every route it guards). Only mcAssignModule's auto-grant-at-install
// writes it today (WORKSPACE_APPS.md ground truth) — nothing let an admin
// narrow it afterward without reassigning the module. These toggle the WORKING
// COPY on `_mcState.settings.workspaceModule` (the same bag the Personalize
// tab edits); mcSavePersonalization('workspaceModule', …) — already wired,
// `workspaceModule` is in MC_SETTINGS_KEYS — persists it through the same
// merge-safe settings key mcAssignModule / mcRollbackModule use.

// Mirrors moduleDataBridge.js ALLOWED_OPS — the ops the records capability
// grant may restrict. Duplicated (not imported — project files don't import
// one another) so keep in sync if that set ever changes.
export const MC_RECORDS_GRANT_OPS = [
  'list',
  'get',
  'create',
  'update',
  'upsert',
  'remove',
  'subscribe'
]

const _moduleOf = (root) =>
  (root._mcState && root._mcState.settings && root._mcState.settings.workspaceModule) || null

// ── Which installed app the Grant tab is looking at ─────────────────────────
// A grant can live in EITHER of two settings slots: the legacy singular
// `workspaceModule` (Tier 3 full-takeover custom module) or an entry of the
// additive `workspaceApps` list (Tier 2 /add-app + marketplace installs — the
// shape every real deployment shipped so far, ICCA + Certification, actually
// uses). The revoke/toggle/sync actions below all resolve "the selected app"
// through the same lookup so they patch EXACTLY the slot it lives in and
// nothing else. Mirrors pages/admin/module.js `_installedApps` — duplicated
// (project files don't import one another); keep the two in sync.
const _lcId = (v) => (v == null ? '' : String(v).toLowerCase())
const _refId = (r) => `${_lcId(r && r.owner)}/${_lcId(r && r.key)}`

const _appRefLocation = (root, ownerKey) => {
  if (!ownerKey) return null
  const settings = (root._mcState && root._mcState.settings) || {}
  const mod = settings.workspaceModule
  if (mod && mod.owner && _refId(mod) === ownerKey) return { src: 'workspaceModule', ref: mod }
  const apps = Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  const idx = apps.findIndex((r) => r && r.owner && _refId(r) === ownerKey)
  return idx >= 0 ? { src: 'workspaceApps', ref: apps[idx], idx } : null
}

// The Grant tab's default selection when the admin hasn't picked one yet —
// the legacy module if assigned, else the first installed app.
const _defaultAppIdFor = (root) => {
  const settings = (root._mcState && root._mcState.settings) || {}
  if (settings.workspaceModule && settings.workspaceModule.owner) {
    return _refId(settings.workspaceModule)
  }
  const apps = Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  const first = apps.find((r) => r && r.owner && r.key)
  return first ? _refId(first) : null
}

// el.call('mcDefaultAppId') — exported so pages/admin/module.js's own
// `_installedApps`/`_selectedApp` (which resolve the SAME default for
// rendering) can stay in lock-step without importing this file; same-file
// callers below use `_defaultAppIdFor` directly.
export function mcDefaultAppId() {
  const el = this
  const root = _consoleRootOf(el)
  return root ? _defaultAppIdFor(root) : null
}

const _selectedAppId = (root) => (root && (root._mcGrantAppId || _defaultAppIdFor(root))) || null

// Patch ONE app ref back into whichever settings key it lives in — the single
// merge-safe write every mutator below shares.
const _patchAppRef = (root, loc, nextRef) => {
  if (loc.src === 'workspaceModule') {
    _patchSettings(root, { workspaceModule: nextRef })
    return
  }
  const settings = (root._mcState && root._mcState.settings) || {}
  const apps = Array.isArray(settings.workspaceApps) ? [...settings.workspaceApps] : []
  apps[loc.idx] = nextRef
  _patchSettings(root, { workspaceApps: apps })
}

// Remove one collection from the SELECTED app's grant entirely (destructive —
// asks). The house confirm, localized (CRUD-CONFIRM-STANDARD D1, 2026-10-30):
// it was a raw `window.confirm` with an English sentence in the code. The
// working copy changes in `onConfirm` only; the grant's Save persists it.
export function mcRevokeRecordsCollection(collection) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const loc = _appRefLocation(root, _selectedAppId(root))
  if (!loc)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.noAppSelected'),
      type: 'error'
    })
  el.call('confirmDialog', {
    title: el
      .call('polyglot', 'admin.module.records.revokeTitle')
      .replace('{collection}', collection),
    message: el.call('polyglot', 'admin.module.records.revokeMessage'),
    confirmText: el.call('polyglot', 'admin.module.records.revoke'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: () => {
      // The dialog is modal: `loc`, read at the click, is still the entry
      // the question was about.
      const rec = (loc.ref.capabilities && loc.ref.capabilities.records) || {
        collections: [],
        ops: []
      }
      // Filter by KEY — a stored entry may be `{ key, read, write }` (F1),
      // which an identity compare against the UI's key string would never
      // match, leaving the "revoked" collection silently granted.
      const collections = (rec.collections || []).filter((c) => recordsEntryKey(c) !== collection)
      const nextRef = {
        ...loc.ref,
        capabilities: { ...(loc.ref.capabilities || {}), records: { ...rec, collections } }
      }
      _patchAppRef(root, loc, nextRef)
    }
  })
}

// Toggle one op for the WHOLE records grant — moduleDataBridge gates every
// granted collection by the same ops set (no per-collection op scoping today).
export function mcToggleRecordsOp(op) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const loc = _appRefLocation(root, _selectedAppId(root))
  if (!loc)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.noAppSelected'),
      type: 'error'
    })
  const rec = (loc.ref.capabilities && loc.ref.capabilities.records) || { collections: [], ops: [] }
  const cur = Array.isArray(rec.ops) && rec.ops.length ? rec.ops : [...MC_RECORDS_GRANT_OPS]
  const turningOff = cur.indexOf(op) >= 0
  if (turningOff && cur.length === 1) {
    // moduleDataBridge.js reads an EMPTY `ops` array as "all ops" (unset =
    // unrestricted) — unchecking the LAST op would silently flip the grant
    // back to unrestricted instead of narrowing it. Revoke the collection
    // instead if the intent is to remove all access.
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.cantRevokeAll'),
      message: el.call('polyglot', 'admin.module.notify.cantRevokeAllBody'),
      type: 'error'
    })
  }
  const ops = turningOff ? cur.filter((o) => o !== op) : [...cur, op]
  const nextRef = {
    ...loc.ref,
    capabilities: { ...(loc.ref.capabilities || {}), records: { ...rec, ops } }
  }
  _patchAppRef(root, loc, nextRef)
}

// el.call('mcSaveSelectedAppGrant') — the records-grant Matrix's Save button.
// Persists whichever settings key the SELECTED app's ref lives in, routed
// through mcSavePersonalization so it gets the same optimistic-patch /
// rollback / notify behavior as every other Personalize save.
export async function mcSaveSelectedAppGrant() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const loc = _appRefLocation(root, _selectedAppId(root))
  if (!loc)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.noAppSelected'),
      type: 'error'
    })
  const settings = (root._mcState && root._mcState.settings) || {}
  const value = loc.src === 'workspaceModule' ? settings.workspaceModule : settings.workspaceApps
  await el.call('mcSavePersonalization', loc.src, value)
}

// ── Collections re-sync ──────────────────────────────────────────────────
// A module can declare a NEW record collection in a LATER version — its ref's
// `capabilities.records.collections` was written ONCE at install
// (mcAssignModule / installWorkspaceAppRef) and the loader resolves
// capabilities from that STORED ref only, never the manifest
// (workspaceProjects.js:371 — "a module cannot self-grant"). Left alone, the
// new collection is refused FOREVER. This is the explicit, operator-consented
// fix: diff what the app now declares against what it's granted, show BOTH
// the additions and the now-stale grants, and write only what the operator
// approves — never auto-follow the manifest.
//
// A changed LEVEL of a collection already granted (production-app 1.8.6 moved
// prod_tasks write 'scoped' → 'acl') is the third list, `changed`
// (mcLevelChanges): the server keeps a stored level it cannot prove the
// manifest's narrows, so it lands only when the admin approves it here (the
// settings PATCH's `approveLevels`, checked by the server against its own read
// of the manifest of the version the entry loads). The check reads the
// manifest of the version THIS install loads — the loader's rule
// (workspaceProjects.js) and the server's (resolveAppNavSource): the ref's
// version pin, else its channel's publish env, else the main head. It names
// that location to the shared reader as plain options (Nika 10-31, N2 pick 1);
// the reader reads `raw: true` (security review N2, shared
// moduleRefFetchRaw.test.js) and the console hands it no fetcher of its own.
export async function mcCheckAppSync(ownerKeyArg) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const ownerKey = ownerKeyArg || _selectedAppId(root)
  const loc = _appRefLocation(root, ownerKey)
  if (!loc)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.noAppToCheck'),
      type: 'error'
    })
  const [owner, key] = ownerKey.split('/')
  root.update({ _mcSyncChecking: ownerKey, _mcSyncError: null })
  try {
    const { wsId } = _ids(root)
    let branch = loc.ref.branch || 'main'
    let version = loc.ref.version || null
    if (!version && loc.ref.channel) {
      try {
        const cfg = await el.call(
          'resolveEnvPublishConfig',
          { owner, key },
          loc.ref.channel,
          wsId ? { workspaceId: wsId } : {}
        )
        if (cfg) {
          branch = cfg.branch || branch
          if (cfg.version && cfg.version !== 'latest') version = cfg.version
        }
      } catch (_) {
        // The loader's fallback: an unreadable channel config loads the head.
      }
    }
    const declared = await el.call('fetchDeclaredCollections', owner, key, {
      branch,
      version: version || 'latest',
      ...(wsId ? { workspaceId: wsId } : {})
    })
    const granted =
      (loc.ref.capabilities &&
        loc.ref.capabilities.records &&
        loc.ref.capabilities.records.collections) ||
      []
    const { added, stale } = diffDeclaredCollections(declared, granted)
    // The singular `workspaceModule` slot has no compare-and-swap, so the
    // server takes no level approval there: no `changed` list for it.
    const changed =
      loc.src === 'workspaceApps' ? el.call('mcLevelChanges', declared, granted) || [] : []
    root.update({
      _mcSyncChecking: null,
      _mcSyncDiff: {
        appId: ownerKey,
        declared,
        granted,
        added,
        stale,
        changed,
        checkedAt: Date.now()
      },
      // Default selection: propose GRANTING every newly-declared collection
      // (the whole point of the check), propose REVOKING none — a revoke
      // removes standing access, the more consequential half of the
      // decision, so it stays an explicit per-collection opt-in. A level
      // change is the same kind of decision: approved one by one, never by
      // default.
      _mcSyncSelection: {
        grant: Object.fromEntries(added.map((c) => [c, true])),
        revoke: Object.fromEntries(stale.map((c) => [c, false])),
        approve: Object.fromEntries(changed.map((c) => [c.key, false]))
      }
    })
  } catch (err) {
    root.update({
      _mcSyncChecking: null,
      _mcSyncError: (err && err.message) || el.call('polyglot', 'admin.module.error.checkFailed')
    })
  }
}

// el.call('mcGrantCanon', entry) — the console's ONE canon of a records grant
// entry: the entry as the server's gate reads it (AccessResolver
// normalizeCollectionGrantEntry, through WorkspaceAppGrants canonGrantEntry),
// case for case — the key trimmed and lower-cased; an absent level is the
// plain default (read member, write editor), an unknown one fails closed to
// admin; the scope field trimmed; team slugs trimmed and lower-cased, an
// unknown team level the plain default, an absent one no rule; field names
// trimmed, an unknown field read admin, an unknown field write server, a
// `perm:<key>` tier kept as it is; maps sorted. → `{ key, read, write,
// scopeField, teams, fields }`, or null for an entry with no usable key. Pure.
// Every reader reaches it through el.call('mcGrantCanon'), so no second copy
// of these rules exists.
export function mcGrantCanon(entry) {
  const READ = ['member', 'admin', 'own', 'scoped', 'acl']
  const WRITE = ['editor', 'admin', 'own', 'scoped', 'acl']
  const isObj = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v)
  const sorted = (m) => {
    const names = Object.keys(m).sort((a, b) => a.localeCompare(b))
    return names.length ? Object.fromEntries(names.map((n) => [n, m[n]])) : null
  }
  const teamsOf = (raw) => {
    if (!isObj(raw)) return null
    const level = (v, valid, plain) => (v === undefined ? undefined : valid.includes(v) ? v : plain)
    const out = {}
    for (const [rawSlug, o] of Object.entries(raw)) {
      const slug = rawSlug.trim().toLowerCase()
      if (!slug || !isObj(o)) continue
      const read = level(o.read, READ, 'member')
      const write = level(o.write, WRITE, 'editor')
      if (read === undefined && write === undefined) continue
      out[slug] = { read, write }
    }
    return sorted(out)
  }
  const fieldsOf = (raw) => {
    if (!isObj(raw)) return null
    const tier = (v, valid, closed) =>
      v === undefined
        ? undefined
        : (typeof v === 'string' && v.startsWith('perm:') && v.length > 5) || valid.includes(v)
          ? v
          : closed
    const out = {}
    for (const [rawName, rule] of Object.entries(raw)) {
      const name = rawName.trim()
      if (!name || !isObj(rule)) continue
      const read = tier(rule.read, ['member', 'own', 'admin'], 'admin')
      const write = tier(rule.write, ['editor', 'admin', 'server'], 'server')
      if (read === undefined && write === undefined) continue
      out[name] = { read, write }
    }
    return sorted(out)
  }
  const canon = (raw) => {
    if (typeof raw === 'string') {
      const key = raw.trim().toLowerCase()
      if (!key) return null
      return { key, read: 'member', write: 'editor', scopeField: null, teams: null, fields: null }
    }
    if (!raw || typeof raw !== 'object' || typeof raw.key !== 'string') return null
    const key = raw.key.trim().toLowerCase()
    if (!key) return null
    return {
      key,
      read: raw.read === undefined ? 'member' : READ.includes(raw.read) ? raw.read : 'admin',
      write: raw.write === undefined ? 'editor' : WRITE.includes(raw.write) ? raw.write : 'admin',
      scopeField:
        typeof raw.scopeField === 'string' && raw.scopeField.trim() ? raw.scopeField.trim() : null,
      teams: teamsOf(raw.teams),
      fields: fieldsOf(raw.fields)
    }
  }
  return canon(entry)
}

// el.call('mcLevelChanges', declared, granted) — the LEVEL changes between the
// manifest's declared collections (fetchDeclaredCollections) and the stored
// records grant: each key in BOTH lists whose entries differ as the server's
// gate reads them (el.call('mcGrantCanon')). So entries equal here are equal
// on the server too; the server, not this, checks an approval against its own
// manifest read. The first entry of a key wins, as on the server.
// → [{ key, now, asks, entry, rules }]: `now` / `asks` the stored and the
// manifest entry as the gate reads them (what the sheet names), `entry` the
// manifest's entry as declared (what an approval sends), `rules` true when a
// team or field rule (or a scope field no level uses) differs.
export function mcLevelChanges(declared, granted) {
  const el = this
  const byKey = (list) => {
    const out = new Map()
    for (const raw of Array.isArray(list) ? list : []) {
      const c = el.call('mcGrantCanon', raw)
      if (c && !out.has(c.key)) out.set(c.key, { raw, c })
    }
    return out
  }
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)
  const stored = byKey(granted)
  const out = []
  for (const [key, d] of byKey(declared)) {
    const s = stored.get(key)
    if (!s || same(s.c, d.c)) continue
    const scopedAny = [s.c.read, s.c.write, d.c.read, d.c.write].includes('scoped')
    out.push({
      key,
      now: s.c,
      asks: d.c,
      entry: d.raw,
      rules:
        !same(s.c.teams, d.c.teams) ||
        !same(s.c.fields, d.c.fields) ||
        (!scopedAny && s.c.scopeField !== d.c.scopeField)
    })
  }
  return out
}

// el.call('mcToggleSyncSelection', 'grant'|'revoke'|'approve', collection) —
// flip one candidate's checkbox in the reviewed-but-not-yet-applied diff
// ('approve' = a level change, keyed by its collection key). No
// persistence — the diff + selection are pure working-copy UI state.
export function mcToggleSyncSelection(kind, collection) {
  const el = this
  const root = _consoleRootOf(el)
  if (!root || (kind !== 'grant' && kind !== 'revoke' && kind !== 'approve')) return
  const sel = root._mcSyncSelection || { grant: {}, revoke: {} }
  const bucket = { ...(sel[kind] || {}) }
  bucket[collection] = !bucket[collection]
  root.update({ _mcSyncSelection: { ...sel, [kind]: bucket } })
}

// el.call('mcApplyAppSync') — THE ONE WRITE. Persists the operator-approved
// subset of the reviewed diff onto `capabilities.records.collections` only —
// applyCollectionsSync's spread leaves `capabilities.ai` / `capabilities.
// files` / tier / channel / version / installMode exactly as they were.
// Patches ONLY the one settings key the ref lives in (`workspaceModule` or
// `workspaceApps`) — no other settings key is read or written, unlike the
// old "just reassign the module" remedy that rewrote (and could reset) the
// whole personalization layer.
export async function mcApplyAppSync() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const diff = root._mcSyncDiff
  if (!diff)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.checkFirst'),
      type: 'error'
    })
  const sel = root._mcSyncSelection || { grant: {}, revoke: {} }
  // `added`/`stale` are collection KEYS (diffDeclaredCollections). A grant
  // writes the DECLARED entry for that key — which may carry per-collection
  // levels (`{ key, read, write }`, F1) — so the app's declared access policy
  // lands on the ref verbatim instead of being flattened to a member-wide
  // string. Revokes prune by key.
  const declared = Array.isArray(diff.declared) ? diff.declared : []
  const grant = (diff.added || [])
    .filter((c) => sel.grant && sel.grant[c])
    .map((k) => declared.find((d) => recordsEntryKey(d) === k) || k)
  const revoke = (diff.stale || []).filter((c) => sel.revoke && sel.revoke[c])
  // An approved LEVEL change (mcLevelChanges): the manifest's entry, as
  // declared, for a key already granted.
  const approve = (diff.changed || []).filter((c) => sel.approve && sel.approve[c.key])
  if (!grant.length && !revoke.length && !approve.length) {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.nothingSelected'),
      message: el.call('polyglot', 'admin.module.notify.nothingSelectedBody'),
      type: 'error'
    })
  }
  const loc = _appRefLocation(root, diff.appId)
  if (!loc)
    return _consoleNotify(el, { title: el.call('polyglot', 'admin.module.notify.appGone'), type: 'error' })
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  if (!sdk || typeof sdk.updateWorkspaceSettings !== 'function' || !wsId) {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      type: 'error'
    })
  }

  // applyCollectionsSync (workspace-shared) keeps its own { add, prune } arg
  // names — that's the shared-library API; only the console's selection
  // buckets are named grant/revoke (see the _mcSyncSelection note up top).
  // An approval alone leaves the grant's `ops` as they are.
  const synced =
    grant.length || revoke.length
      ? applyCollectionsSync(loc.ref, { add: grant, prune: revoke })
      : loc.ref
  // Each approved key takes the manifest's entry IN PLACE, and the entry
  // names the keys it approves (`approveLevels`, request-only: the server
  // checks each against its own read of the manifest, lands its entry, and
  // never stores the field). Only the plural list carries an approval.
  const nextRef =
    approve.length && loc.src === 'workspaceApps'
      ? (() => {
          const asks = new Map(approve.map((c) => [c.key, c.entry]))
          const rec = (synced.capabilities && synced.capabilities.records) || {}
          const collections = (Array.isArray(rec.collections) ? rec.collections : []).map((e) => {
            const k = recordsEntryKey(e).trim().toLowerCase()
            return asks.has(k) ? asks.get(k) : e
          })
          return {
            ...synced,
            capabilities: { ...(synced.capabilities || {}), records: { ...rec, collections } },
            approveLevels: approve.map((c) => c.key)
          }
        })()
      : synced
  const patchValue =
    loc.src === 'workspaceModule'
      ? nextRef
      : (() => {
          const settings = (root._mcState && root._mcState.settings) || {}
          const apps = Array.isArray(settings.workspaceApps) ? [...settings.workspaceApps] : []
          apps[loc.idx] = nextRef
          return apps
        })()
  const patch = { [loc.src]: patchValue }
  const prev = _patchSettings(root, patch)

  _setBusy(root, 'personalize', true)
  try {
    // The sync IS a capability change, so it must carry the precondition —
    // without it the server would preserve the stored grant and this write
    // would land as a no-op.
    const res = await sdk.updateWorkspaceSettings(wsId, _appsPatch(root, patch))
    if (res && res.success === false) throw new Error(res.message)
    // An approval lands the SERVER's entry and never stores `approveLevels`:
    // the answer's list (else this payload without the field) is what is
    // stored now — the view and the baseline, so no later save re-sends it.
    if (approve.length && loc.src === 'workspaceApps') {
      const stored =
        res && res.settings && Array.isArray(res.settings.workspaceApps)
          ? res.settings.workspaceApps
          : patchValue.map((r) => {
              if (!r || typeof r !== 'object') return r
              const copy = { ...r }
              delete copy.approveLevels
              return copy
            })
      _patchSettings(root, { workspaceApps: stored })
      _rebaseApps(root, stored)
    } else if (loc.src === 'workspaceApps') _rebaseApps(root, patchValue)
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.grantSynced'),
      message: [
        grant.length
          ? el.call('polyglot', 'admin.module.notify.grantedCount').replace('{count}', grant.length)
          : null,
        revoke.length
          ? el
              .call('polyglot', 'admin.module.notify.revokedCount')
              .replace('{count}', revoke.length)
          : null,
        approve.length
          ? el
              .call('polyglot', 'admin.module.notify.approvedCount')
              .replace('{count}', approve.length)
          : null
      ]
        .filter(Boolean)
        .join(', '),
      type: 'success'
    })
    root.update({ _mcSyncDiff: null, _mcSyncSelection: null })
  } catch (err) {
    _patchSettings(root, { [loc.src]: prev })
    // The server refused an approval: the app declares another level now than
    // the one the admin saw (or its manifest could not be read). Nothing was
    // written — check again, so the sheet shows what the app asks for now.
    const refusal = err && err.cause && err.cause.error
    if (refusal === 'level_approval_stale' || refusal === 'level_approval_unverifiable') {
      _consoleNotify(el, {
        title: el.call('polyglot', 'admin.module.notify.levelStale'),
        message: el.call('polyglot', 'admin.module.notify.levelStaleBody'),
        type: 'error'
      })
      await el.call('mcCheckAppSync', diff.appId)
    } else await _handleSaveError(el, root, err, 'admin.module.notify.syncFailed')
  } finally {
    _setBusy(root, 'personalize', false)
  }
}

// ── Legacy single-slot → plural app-list migration ──────────────────────────
// el.call('mcMigrateLegacyAppInstall') — one-time, operator-triggered move of
// a pre-plural app install out of the singular `workspaceModule` slot and into
// the plural `workspaceApps[]` list, so it's managed identically to every app
// installed after the 2026-07-23 plural core (this console's own Grant-tab
// AppPicker, the /add-app rail's per-app remove, uninstall) instead of the
// singular-module slot it happens to still occupy.
//
// A no-op unless `workspaceModule` is a PLURAL-shaped ref (`installMode:
// 'app'`, written by the /add-app or marketplace install paths) — a REAL
// full-takeover client module (greenig/bellforge/financials — installMode
// unset) is untouched; that slot stays reserved for it
// (workspaceConfigs.js._isAppModeInstall is the same test).
//
// Both loaders (workspaceProjects.js loadWorkspaceProject/loadWorkspaceApps)
// and resolveWorkspaceConfig already treat the two shapes identically, so this
// changes WHICH settings key the ref lives in, never runtime behavior — a
// single-workspace, operator-consented write through the same
// updateWorkspaceSettings path every other console action here already uses,
// not a blanket/background migration over live customer data
// (tickets/workspace.md WS-APPS follow-up #5).
export async function mcMigrateLegacyAppInstall() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const settings = (root._mcState && root._mcState.settings) || {}
  const mod = settings.workspaceModule
  if (!mod || mod.installMode !== 'app') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.nothingToMigrate'),
      message: el.call('polyglot', 'admin.module.notify.nothingToMigrateBody'),
      type: 'error'
    })
  }
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  if (!sdk || typeof sdk.updateWorkspaceSettings !== 'function' || !wsId) {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      type: 'error'
    })
  }

  const prevModule = mod
  const prevApps = Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  const nextApps = upsertWorkspaceApp(prevApps, mod)
  const patch = { workspaceModule: null, workspaceApps: nextApps }
  _patchSettings(root, patch)
  _setBusy(root, 'personalize', true)
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, _appsPatch(root, patch))
    if (res && res.success === false) throw new Error(res.message)
    _rebaseApps(root, nextApps)

    // Live-sync the shell the same way mcResetNavbar does — null/replace on
    // the workspace RECORD (loadWorkspaceProject/loadWorkspaceApps resolve
    // refs from root.workspace.settings) and on root.workspaceSettings (the
    // reactive cache applyWorkspaceConfig reads), then re-run both loaders so
    // the app's routes/nav move over to the plural path immediately.
    // WRITE-BASE site (spec §5). `{ ...(ws.settings || {}), ...patch }` merges
    // over a base that may not exist: on a list-shaped record it FABRICATES a
    // settings bag containing only the key just written, and every raw reader
    // downstream then treats that one-key bag as the workspace's whole
    // configuration (loadWorkspaceProject/loadWorkspaceApps did exactly that
    // via `!!ws.settings` until this same spec §5 pass). The server write above
    // has already landed and is per-key merge-safe, so ONLY the local mirror is
    // conditional here — never the persistence.
    const base = workspaceSettingsForWrite(root)
    const localSync = {
      // `root.workspaceSettings` is a DIFFERENT store — the derived bag
      // applyWorkspaceConfig publishes and HeaderTabs reads reactively. It has
      // no record shape to corrupt, and the re-runs below republish it anyway,
      // so it syncs unconditionally.
      workspaceSettings: { ...(root.workspaceSettings || {}), ...patch },
      _mcGrantAppId: _refId(mod)
    }
    if (base) localSync.workspace = { ...root.workspace, settings: { ...base, ...patch } }
    else invalidateWorkspaceCache(wsId)
    root.update(localSync)
    await loadWorkspaceProject(root, sdk)
    await loadWorkspaceApps(root, sdk)
    applyWorkspaceConfig(root)
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.migrated'),
      message: el
        .call('polyglot', 'admin.module.notify.migratedBody')
        .replace('{module}', `${mod.owner}/${mod.key}`),
      type: 'success'
    })
  } catch (err) {
    _patchSettings(root, { workspaceModule: prevModule, workspaceApps: prevApps })
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.migrationFailed'),
      message: (err && err.message) || '',
      type: 'error'
    })
  } finally {
    _setBusy(root, 'personalize', false)
  }
}

// ── designSystem editor: validate JSON + shape (preview-before-apply) ──────
// Parses _mcDs.text, validates the {canvasBg, vars} shape, and stores the
// parsed object + validation flag so the Preview panel can render it and the
// Apply button (mcSavePersonalization('designSystem', parsed)) can enable —
// we NEVER persist unvalidated / invalid JSON.
export function mcValidateDesignSystem() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const ds = root._mcDs || {}
  const text = ds.text || ''
  if (!text.trim()) {
    root.update({
      _mcDs: {
        ...ds,
        error: el.call('polyglot', 'admin.module.error.dsEmpty'),
        parsed: null,
        validated: false
      }
    })
    return
  }
  let parsed
  try {
    parsed = JSON.parse(text)
  } catch (err) {
    root.update({
      _mcDs: {
        ...ds,
        error: el
          .call('polyglot', 'admin.module.error.dsInvalidJson')
          .replace('{detail}', (err && err.message) || ''),
        parsed: null,
        validated: false
      }
    })
    return
  }
  const shapeErr = _validateDsShape(parsed, el)
  if (shapeErr) {
    root.update({ _mcDs: { ...ds, error: shapeErr, parsed: null, validated: false } })
    return
  }
  root.update({ _mcDs: { ...ds, error: null, parsed, validated: true } })
  _consoleNotify(el, {
    title: el.call('polyglot', 'admin.module.notify.dsValid'),
    message: el.call('polyglot', 'admin.module.notify.dsValidBody'),
    type: 'success'
  })
}

// ── 6. Uninstall the module (detach + reset personalization) ───────────────
// Detaches settings.workspaceModule (the server already treats `null` as
// detach — WorkspaceController.validateWorkspaceModuleRef) and, in the same
// spirit as mcAssignModule's auto-grant-at-install, resets the
// personalization layer the module was steering (navbar/apps/
// home_default_panels) back to framework defaults. Since workspaceModule
// itself is ALSO nulled here (no module stays assigned), there is no
// configDefaults left to fall back to — these four nulls land on the bare
// shell default, same as mcResetNavbar's null-as-absent handling
// (workspaceProjects.js _stripNullish) with nothing installed.
export function mcRemoveModule() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  const mod = _moduleOf(root)
  if (!mod)
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.noModule'),
      type: 'error'
    })
  if (typeof sdk.updateWorkspaceSettings !== 'function') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      message: el.call('polyglot', 'admin.module.notify.sdkNotReadyBody'),
      type: 'error'
    })
  }

  el.call('confirmDialog', {
    title: el.call('polyglot', 'admin.module.notify.uninstallConfirm'),
    message: el
      .call('polyglot', 'admin.module.notify.uninstallConfirmBody')
      .replace('{module}', `${mod.owner}/${mod.key}`),
    confirmText: el.call('polyglot', 'admin.module.notify.uninstallCta'),
    // The D1 shape (CRUD-CONFIRM-STANDARD, 2026-10-30): `'red'` is not a
    // brand theme and rendered unstyled; the title already says what goes.
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: async () => {
      // Snapshot the FULL working-copy settings (not just the first patched
      // key — _patchSettings only returns one prior value) so a failure can
      // roll every one of the four reset keys back, not just workspaceModule.
      const prevSettings = (root._mcState && root._mcState.settings) || {}
      const reset = { workspaceModule: null, navbar: null, apps: null, home_default_panels: null }
      _patchSettings(root, reset)
      _setBusy(root, 'uninstall', true)
      try {
        const res = await sdk.updateWorkspaceSettings(wsId, reset)
        if (res && res.success === false) throw new Error(res.message)
        await mcLoadState.call(el)
        _consoleNotify(el, {
          title: el
            .call('polyglot', 'admin.module.notify.uninstalled')
            .replace('{module}', `${mod.owner}/${mod.key}`),
          type: 'success'
        })
      } catch (err) {
        root.update({ _mcState: { ...(root._mcState || {}), settings: prevSettings } })
        _consoleNotify(el, {
          title: el.call('polyglot', 'admin.module.notify.uninstallFailed'),
          message:
            (err && err.message) || el.call('polyglot', 'admin.module.notify.uninstallFailedBody'),
          type: 'error'
        })
      } finally {
        _setBusy(root, 'uninstall', false)
      }
    }
  })
}

// ── 7. Reset navbar to defaults (Personalize tab) ───────────────────────────
// Persists `navbar:null` — clears this workspace's OWN personalization, not a
// hard revert to the shell default. loadWorkspaceProject's config merge
// (workspaceProjects.js _stripNullish) treats a null settings key as absent,
// so a null falls through to the assigned module's own configDefaults.navbar
// when one is installed, and only lands on the bare shell default
// (DEFAULT_NAVBAR) when no module is assigned or the module declares none —
// see tickets/workspace.md "settings.navbar:null suppresses an assigned
// module's configDefaults.navbar". `null` (rather than a true $unset) is the
// merge-safe settings writer's only "clear this key" signal today — it has no
// $unset op yet (tickets/server.md); the stored row keeps an explicit `null`
// rather than truly dropping the field, but every reader now treats the two
// identically.
// ── 7b. Reset ALL personalization to defaults (Personalize tab) ─────────────
// Nika 2026-07-25 ("reset to default does not reset navbar and / page
// widgets"): the old button only cleared `settings.navbar`. This clears the
// full personalization surface in ONE pass:
//   • workspace `settings.navbar` + `settings.home_default_panels` → null
//     (merge-safe per-key $set; null = cleared-key sentinel, resolution
//     falls through to module configDefaults / shell defaults)
//   • the CURRENT USER's home dashboard prefs (grid_layout/hidden_widgets
//     on home_dashboard_prefs) + the `symbols.home.*` localStorage seeds —
//     the same payload the home Customize drawer's own reset writes, so
//     the / page widgets actually return to the default set
// then live-syncs the shell exactly like mcResetNavbar (record +
// workspaceSettings mirrors, loader re-run, config re-resolve).
//
// It resets the navbar and the home widgets for EVERYONE, so it asks first
// through the house confirm (CRUD-CONFIRM-STANDARD D1, 2026-10-30 — it wrote
// on one click; the /add-app twin `resetWorkspaceNavbar` already asked). The
// write runs in `onConfirm` only.
export function mcResetPersonalization() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  if (typeof sdk.updateWorkspaceSettings !== 'function') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      message: el.call('polyglot', 'admin.module.notify.sdkNotReadyBody'),
      type: 'error'
    })
  }
  el.call('confirmDialog', {
    title: el.call('polyglot', 'admin.module.navbar.resetConfirmTitle'),
    message: el.call('polyglot', 'admin.module.navbar.resetConfirmMessage'),
    confirmText: el.call('polyglot', 'admin.module.navbar.reset'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: () => _resetPersonalizationNow(el, root, sdk, wsId)
  })
}

async function _resetPersonalizationNow(el, root, sdk, wsId) {
  const _st0 = (root._mcState && root._mcState.settings) || {}
  const prevNavbar = _st0.navbar
  const prevPanels = _st0.home_default_panels
  _patchSettings(root, { navbar: null, home_default_panels: null })
  _setBusy(root, 'personalize', true)
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, { navbar: null, home_default_panels: null })
    if (res && res.success === false) throw new Error(res.message)

    // Per-user / page widgets — mirror the Customize drawer's reset: wipe
    // the localStorage seeds and null the server row's layout + hidden set
    // (no dashboard_v stamp needed: a null layout + empty hidden set IS the
    // base default regardless of version gating).
    try {
      Object.keys(globalThis.localStorage || {})
        .filter((k) => k.startsWith('symbols.home.'))
        .forEach((k) => globalThis.localStorage.removeItem(k))
    } catch (_) {
      /* private mode */
    }
    const prefsNext = { grid_layout: null, hidden_widgets: {} }
    root.update({ userDashboardPrefs: prefsNext, _homeHiddenWidgets: {} })
    try {
      await sdk.execute('workspaceProject.homeDashboardPrefs', 'update', { payload: prefsNext })
    } catch (err) {
      console.warn('[moduleConsole] dashboard prefs reset failed:', err?.message || err)
    }

    // WRITE-BASE site (spec §5) — same reasoning as mcMigrateLegacyAppInstall's
    // mirror above: merging the two nulled keys over an absent base publishes a
    // `settings` bag that describes nothing but the reset itself, on a record
    // that never had one. The server reset already succeeded; skipping the
    // mirror costs only the local paint, and the loaders re-run right below.
    const base = workspaceSettingsForWrite(root)
    const localSync = {
      workspaceSettings: {
        ...(root.workspaceSettings || {}),
        navbar: null,
        home_default_panels: null
      }
    }
    if (base) {
      localSync.workspace = {
        ...root.workspace,
        settings: { ...base, navbar: null, home_default_panels: null }
      }
    } else invalidateWorkspaceCache(wsId)
    root.update(localSync)
    await loadWorkspaceProject(root, sdk)
    await loadWorkspaceApps(root, sdk)
    applyWorkspaceConfig(root)
    _consoleNotify(el, { title: el.call('polyglot', 'admin.module.notify.resetAll'), type: 'success' })
  } catch (err) {
    _patchSettings(root, { navbar: prevNavbar, home_default_panels: prevPanels })
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.resetFailed'),
      message: (err && err.message) || '',
      type: 'error'
    })
  } finally {
    _setBusy(root, 'personalize', false)
  }
}

export async function mcResetNavbar() {
  const el = this
  const root = _consoleRootOf(el)
  if (!root) return
  const sdk = _sdkOf(el)
  const { wsId } = _ids(root)
  if (typeof sdk.updateWorkspaceSettings !== 'function') {
    return _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.sdkNotReady'),
      message: el.call('polyglot', 'admin.module.notify.sdkNotReadyBody'),
      type: 'error'
    })
  }

  const prev = _patchSettings(root, { navbar: null })
  _setBusy(root, 'personalize', true)
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, { navbar: null })
    if (res && res.success === false) throw new Error(res.message)
    // Live-sync the shell so the header reflects the reset without a reload —
    // the same record + resolved-cache sync workspaceApps.js
    // resetWorkspaceNavbar/uninstallWorkspaceApp perform: null the key on the
    // workspace RECORD (HeaderTabs' first-paint source, and what
    // loadWorkspaceProject resolves settings from) and on
    // root.workspaceSettings (HeaderTabs' reactive reader), re-run the
    // project loader so the cached workspaceProject.config drops the old
    // saved navbar (it merged the pre-reset settings at load time), then
    // re-resolve so the module's configDefaults.navbar / DEFAULT_NAVBAR
    // paints immediately.
    // WRITE-BASE site (spec §5) — third instance of the same mirror, same
    // guard. The navbar is the surface where the fabricated bag was most
    // visible: a one-key `{navbar: null}` bag on a lean record reads as "this
    // workspace has settings, and its navbar is explicitly cleared", which is
    // indistinguishable from a workspace that really did reset its navbar.
    const base = workspaceSettingsForWrite(root)
    const localSync = { workspaceSettings: { ...(root.workspaceSettings || {}), navbar: null } }
    if (base) localSync.workspace = { ...root.workspace, settings: { ...base, navbar: null } }
    else invalidateWorkspaceCache(wsId)
    root.update(localSync)
    await loadWorkspaceProject(root, sdk)
    await loadWorkspaceApps(root, sdk)
    applyWorkspaceConfig(root)
    _consoleNotify(el, { title: el.call('polyglot', 'admin.module.notify.resetNavbar'), type: 'success' })
  } catch (err) {
    _patchSettings(root, { navbar: prev })
    _consoleNotify(el, {
      title: el.call('polyglot', 'admin.module.notify.resetFailed'),
      message: (err && err.message) || '',
      type: 'error'
    })
  } finally {
    _setBusy(root, 'personalize', false)
  }
}
